import { KeyboardEvent, useState } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { IconBuilder } from '@components/icons/builder';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

type AiBuilderQuestionsProps = {
  questions: string[];
  onSubmit: (answers: string[]) => void;
};

export function AiBuilderQuestions({ questions, onSubmit }: AiBuilderQuestionsProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<string[]>(Array(questions.length).fill(''));
  const { t } = useScopedTranslate(locale);

  const currentQuestion = questions[currentIndex];
  const currentAnswer = answers[currentIndex];
  const isLastQuestion = currentIndex === questions.length - 1;
  const isFirstQuestion = currentIndex === 0;
  const canProceed = currentAnswer.trim().length > 0;

  const handleNext = () => {
    if (canProceed && !isLastQuestion) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (!isFirstQuestion) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleSubmit = () => {
    if (canProceed && isLastQuestion) {
      onSubmit(answers);
    }
  };

  const handleAnswerChange = (value: string) => {
    const newAnswers = [...answers];
    newAnswers[currentIndex] = value;
    setAnswers(newAnswers);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!canProceed) {
        return;
      }
      if (isLastQuestion) {
        onSubmit(answers);
      } else {
        setCurrentIndex(currentIndex + 1);
      }
    }
  };

  return (
    <QuestionsCard>
      <QuestionsHeader>
        <Stack orientation="horizontal" space={2} align="center">
          <IconBuilder color="elementPurple80" size={16} />
          <Text
            font="semibold14"
            text={t('questionProgress', {
              current: (currentIndex + 1).toString(),
              total: questions.length.toString(),
            })}
            color="textHelper"
          />
        </Stack>
      </QuestionsHeader>

      <QuestionText>
        <Text font="regular16" text={currentQuestion} color="textPrimary" />
      </QuestionText>

      <AnswerInput
        type="text"
        placeholder={t('answerPlaceholder')}
        value={currentAnswer}
        onChange={(e) => {
          return handleAnswerChange(e.target.value);
        }}
        onKeyDown={handleKeyDown}
      />

      {!canProceed && <Text font="regular12" text={t('pleaseAnswer')} color="elementRed100" />}

      <ButtonRow>
        <Button variant="ghost" onClick={handlePrevious} disabled={isFirstQuestion}>
          {t('previousQuestion')}
        </Button>

        {!isLastQuestion && (
          <Button variant="primary" onClick={handleNext} disabled={!canProceed}>
            {t('nextQuestion')}
          </Button>
        )}

        {isLastQuestion && (
          <Button variant="primary" onClick={handleSubmit} disabled={!canProceed}>
            {t('submitAnswers')}
          </Button>
        )}
      </ButtonRow>
    </QuestionsCard>
  );
}

const QuestionsCard = styled.div({
  width: '100%',
  border: `1px solid ${theme.color.border10}`,
  borderRadius: pxToRem(8),
  padding: pxToRem(16),
  background: theme.color.backgroundLightDark,
});

const QuestionsHeader = styled.div({
  marginBottom: pxToRem(12),
});

const QuestionText = styled.div({
  marginBottom: pxToRem(16),
});

const AnswerInput = styled.input({
  width: '100%',
  padding: pxToRem(12),
  border: `1px solid ${theme.color.border10}`,
  borderRadius: pxToRem(4),
  fontSize: pxToRem(14),
  marginBottom: pxToRem(12),
  background: theme.color.backgroundPrimary,
  color: theme.color.textPrimary,
  '&:focus': {
    outline: 'none',
    borderColor: theme.color.elementPurple80,
  },
  '&::placeholder': {
    color: theme.color.textHelper,
  },
});

const ButtonRow = styled(Stack).attrs({
  orientation: 'horizontal',
  justify: 'space-between',
})({
  marginTop: pxToRem(8),
});

const locale = {
  en: {
    questionProgress: 'Question {current} of {total}',
    answerPlaceholder: 'Type your answer...',
    previousQuestion: 'Previous',
    nextQuestion: 'Next',
    submitAnswers: 'Submit Answers',
    pleaseAnswer: 'Please provide an answer to continue',
  },
};
